import { HTMLAttributes, KeyboardEvent, ReactElement, cloneElement } from 'react';
import { Badge, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconArrowsExchange, IconCake, IconRun, IconSoccerField } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useLanguage } from '../../providers/LanguageContext';
import { TrainingEventType } from '../../types/training-event.type';
import { CalendarEntry } from './homeTypes';
import { RENTAL_STATUS_LABELS, renterLabel } from '../../types/inventory.type';

const TRAINING_TYPE_COLOR: Record<TrainingEventType, string> = {
  [TrainingEventType.TRAINING]: 'teal',
  [TrainingEventType.MATCH]: 'indigo',
  [TrainingEventType.EVENT]: 'orange',
};

const fmtTime = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;

export function CalendarItem({ item, titleSize, clubName, onOpen }: {
  item: CalendarEntry;
  titleSize: 'xs' | 'sm';
  clubName: string;
  // Set when the user may edit what the entry stands for (training/event,
  // person of a birthday, ...): the entry becomes clickable.
  onOpen?: () => void;
}) {
  const { t } = useLanguage();
  const rendered = renderEntry(item, titleSize, clubName, t);
  const details = rendered.details;
  const content = onOpen
    ? cloneElement(rendered.content as ReactElement<HTMLAttributes<HTMLElement>>, {
        onClick: onOpen,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter') onOpen();
        },
        style: { cursor: 'pointer' },
      })
    : rendered.content;

  // Entries are truncated in narrow calendar cells — the tooltip shows the full text.
  return (
    <Tooltip
      label={<Stack gap={0}>{details.map((line, i) => <Text key={i} size="xs" fw={i === 0 ? 600 : 400}>{line}</Text>)}</Stack>}
      multiline
      maw={280}
      openDelay={300}
      withArrow
    >
      {content}
    </Tooltip>
  );
}

type Translations = ReturnType<typeof useLanguage>['t'];

function renderEntry(
  item: CalendarEntry,
  titleSize: 'xs' | 'sm',
  clubName: string,
  t: Translations,
): { content: ReactElement; details: string[] } {
  const iconSize = titleSize === 'sm' ? 16 : 14;

  if (item.kind === 'match') {
    const m = item.match;
    const isHome = !clubName || m.homeTeamName.startsWith(clubName);
    const opponent = isHome ? m.awayTeamName : m.homeTeamName;
    const status = m.isCancelled
      ? t.home.cancelled
      : m.isCompleted
        ? `${m.homeGoals ?? 0}:${m.awayGoals ?? 0}`
        : dayjs(m.matchDate).format('HH:mm');
    return {
      details: [
        `${m.homeTeamName} – ${m.awayTeamName}`,
        `${dayjs(m.matchDate).format('dd DD.MM.YYYY')} · ${status}`,
        [m.team, m.competition].filter(Boolean).join(' · '),
        m.venue ?? '',
      ].filter(Boolean),
      content: (
        <Group gap={4} wrap="nowrap">
          <IconSoccerField size={iconSize} style={{ flexShrink: 0 }} />
          <Text size={titleSize} fw={500} truncate style={{ flex: 1, minWidth: 0 }}>{isHome ? 'H' : 'A'}: {opponent}</Text>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{status}</Text>
          <Badge size="xs" variant="light" color="indigo" style={{ flexShrink: 0 }}>{m.team}</Badge>
        </Group>
      ),
    };
  }

  if (item.kind === 'rental') {
    const r = item.rental;
    const variant = r.variant.label !== 'Standard' ? ` (${r.variant.label})` : '';
    return {
      details: [
        `${r.article.name}${variant}`,
        `${dayjs(r.plannedFrom).format('dd DD.MM. HH:mm')} – ${dayjs(r.plannedTo).format('dd DD.MM. HH:mm')}`,
        `${RENTAL_STATUS_LABELS[r.status]}: ${renterLabel(r)}`,
        r.notes ?? '',
      ].filter(Boolean),
      content: (
        <Group gap={4} wrap="nowrap">
          <IconArrowsExchange size={iconSize} style={{ flexShrink: 0 }} />
          <Text size={titleSize} fw={500} truncate style={{ flex: 1, minWidth: 0 }}>{r.article.name}</Text>
          <Text size="xs" c="dimmed" truncate style={{ maxWidth: 90 }}>{renterLabel(r)}</Text>
          <Badge size="xs" variant="light" color={r.status === 'out' ? 'orange' : 'blue'} style={{ flexShrink: 0 }}>
            {r.status === 'out' ? 'Aus' : 'Res.'}
          </Badge>
        </Group>
      ),
    };
  }

  if (item.kind === 'birthday') {
    const b = item.birthday;
    return {
      details: [`${b.firstName} ${b.lastName}`, `${b.age} ${t.home.yearsOld}`],
      content: (
        <Group gap={4} wrap="nowrap">
          <IconCake size={iconSize} style={{ flexShrink: 0 }} />
          <Text size={titleSize} fw={500} truncate style={{ flex: 1, minWidth: 0 }}>{b.firstName} {b.lastName}</Text>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{b.age} {t.home.yearsOld}</Text>
        </Group>
      ),
    };
  }

  const ev = item.training;
  const firstTag = ev.tags?.[0];
  const typeLabel: Record<TrainingEventType, string> = {
    [TrainingEventType.TRAINING]: t.home.typeTraining,
    [TrainingEventType.MATCH]: t.home.typeMatch,
    [TrainingEventType.EVENT]: t.home.typeEvent,
  };
  const time = ev.isCancelled ? t.home.cancelled : `${fmtTime(ev.startTime)} – ${fmtTime(ev.startTime + ev.duration)}`;
  const trainer = ev.assignedPerson ? `${ev.assignedPerson.firstName} ${ev.assignedPerson.lastName}` : '';
  return {
    details: [
      ev.notes || typeLabel[ev.eventType],
      time,
      (ev.tags ?? []).map((tag) => tag.name).join(', '),
      trainer,
    ].filter(Boolean),
    content: (
      <Group gap={4} wrap="nowrap">
        {ev.notes ? (
          <Text size={titleSize} fw={500} truncate style={{ flex: 1, minWidth: 0 }}>{ev.notes}</Text>
        ) : (
          <IconRun size={iconSize} style={{ flexShrink: 0 }} />
        )}
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {ev.isCancelled ? t.home.cancelled : fmtTime(ev.startTime)}
        </Text>
        {firstTag && (
          <Badge size="xs" variant="light" color={firstTag.color || 'teal'} style={{ flexShrink: 0 }}>{firstTag.name}</Badge>
        )}
        <Badge size="xs" variant="light" color={TRAINING_TYPE_COLOR[ev.eventType]} style={{ flexShrink: 0 }}>
          {typeLabel[ev.eventType]}
        </Badge>
      </Group>
    ),
  };
}
